import { useState, useCallback, useEffect, useMemo } from "react";
import drawSpaceConfig from "../config/drawSpaceConfig";

export default function useDrawSpaceEditor({
  activeContextId,
  entities,
  updateEntities,
}) {
  const [tool, setTool] = useState(drawSpaceConfig.tools.SELECT);
  const [selectedId, setSelectedId] = useState(null);

  const safeEntities = useMemo(
    () => (Array.isArray(entities) ? entities : []),
    [entities]
  );

  const commitEntities = useCallback(
    (nextEntities) => {
      if (!activeContextId || typeof updateEntities !== "function") return;

      updateEntities(
        activeContextId,
        Array.isArray(nextEntities) ? nextEntities : []
      );
    },
    [activeContextId, updateEntities]
  );

  const translateGeometry = useCallback((geometry, dx, dy) => {
    if (!geometry) return geometry;

    const next = { ...geometry };

    if (next.x !== undefined) next.x += dx;
    if (next.y !== undefined) next.y += dy;

    if (next.cx !== undefined) next.cx += dx;
    if (next.cy !== undefined) next.cy += dy;

    if (next.x1 !== undefined) next.x1 += dx;
    if (next.y1 !== undefined) next.y1 += dy;
    if (next.x2 !== undefined) next.x2 += dx;
    if (next.y2 !== undefined) next.y2 += dy;

    if (Array.isArray(next.points)) {
      next.points = next.points.map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      }));
    }

    return next;
  }, []);

  const selectEntity = useCallback((id) => {
    setSelectedId(id ?? null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
  }, []);

  useEffect(() => {
    if (!selectedId) return;

    const stillExists = safeEntities.some((entity) => entity?.id === selectedId);
    if (!stillExists) {
      setSelectedId(null);
    }
  }, [safeEntities, selectedId]);

  useEffect(() => {
    setSelectedId(null);
  }, [activeContextId]);

  const replaceEntity = useCallback(
    (updatedEntity) => {
      if (!updatedEntity?.id) return;

      const exists = safeEntities.some((entity) => entity.id === updatedEntity.id);
      if (!exists) return;

      const next = safeEntities.map((entity) =>
        entity.id === updatedEntity.id ? updatedEntity : entity
      );

      commitEntities(next);
    },
    [safeEntities, commitEntities]
  );

  const moveEntity = useCallback((id, dx, dy) => {
  if (!id) return;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
  if (dx === 0 && dy === 0) return;

  const next = safeEntities.map((entity) => {
    if (entity.id !== id) return entity;

    const g = entity.geometry;
    if (!g) return entity;

    let nextGeometry;

    switch (entity.type) {
      case drawSpaceConfig.entityTypes.AREA_RECT:
        nextGeometry = {
          ...g,
          x: g.x + dx,
          y: g.y + dy,
        };
        break;

      case drawSpaceConfig.entityTypes.CIRCLE:
        nextGeometry = {
          ...g,
          cx: g.cx + dx,
          cy: g.cy + dy,
        };
        break;

      case drawSpaceConfig.entityTypes.LINE:
        nextGeometry = {
          ...g,
          x1: g.x1 + dx,
          y1: g.y1 + dy,
          x2: g.x2 + dx,
          y2: g.y2 + dy,
        };
        break;

      case drawSpaceConfig.entityTypes.AREA_POLYLINE:
        nextGeometry = {
          ...g,
          points: (g.points ?? []).map((p) => ({
            x: p.x + dx,
            y: p.y + dy,
          })),
        };
        break;

      case drawSpaceConfig.entityTypes.POINT:
        nextGeometry = {
          ...g,
          x: g.x + dx,
          y: g.y + dy,
        };
        break;

      default:
        return entity;
    }

    return {
      ...entity,
      geometry: nextGeometry,
    };
  });

  commitEntities(next);
}, [safeEntities, commitEntities]);

const copyEntity = useCallback((id, dx, dy) => {
  if (!id) return;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;

  const source = safeEntities.find((e) => e.id === id);
  if (!source) return;

  const newId = `${source.id}_copy_${Date.now()}`;

  const g = source.geometry;
  if (!g) return;

  let nextGeometry;

  switch (source.type) {
    case drawSpaceConfig.entityTypes.AREA_RECT:
      nextGeometry = {
        ...g,
        x: g.x + dx,
        y: g.y + dy,
      };
      break;

    case drawSpaceConfig.entityTypes.CIRCLE:
      nextGeometry = {
        ...g,
        cx: g.cx + dx,
        cy: g.cy + dy,
      };
      break;

    case drawSpaceConfig.entityTypes.LINE:
      nextGeometry = {
        ...g,
        x1: g.x1 + dx,
        y1: g.y1 + dy,
        x2: g.x2 + dx,
        y2: g.y2 + dy,
      };
      break;

    case drawSpaceConfig.entityTypes.AREA_POLYLINE:
      nextGeometry = {
        ...g,
        points: (g.points ?? []).map((p) => ({
          x: p.x + dx,
          y: p.y + dy,
        })),
      };
      break;

    case drawSpaceConfig.entityTypes.POINT:
      nextGeometry = {
        ...g,
        x: g.x + dx,
        y: g.y + dy,
      };
      break;

    default:
      return;
  }

  const newEntity = {
    ...source,
    id: newId,
    geometry: nextGeometry,
  };

  const next = [...safeEntities, newEntity];

  commitEntities(next);
  setSelectedId(newId);
}, [safeEntities, commitEntities]);

const deleteEntity = useCallback((id) => {
  if (!id) return;

  const exists = safeEntities.some((entity) => entity.id === id);
  if (!exists) return;

  const next = safeEntities.filter((entity) => entity.id !== id);

  commitEntities(next);

  if (selectedId === id) {
    setSelectedId(null);
  }
}, [safeEntities, commitEntities, selectedId]);


  return {
  tool,
  setTool,

  selectedId,
  selectEntity,
  clearSelection,

  replaceEntity,
  moveEntity,
moveEntityInContext: moveEntity,

copyEntity,
copyEntityInContext: copyEntity,

deleteEntity,
  deleteEntityInContext: deleteEntity,

translateGeometry,
};
}